import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Box, Stack} from '@mui/material';
import {
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import translate from '@/utils/translate';
import useNavigation from '@/hooks/use-navigation';
import contentStudioStore, {
  contentStudioActions,
  SelectedAsset,
} from '@/stores/contentStudioStore';
import ThumbnailDisplay from '../file-upload/ThumbnailDisplay';

function ManyToManyEditor({
  children,
  flowName,
  asset_page_route,
  backgroundColor = designSystemToken('semantic.bg.primary'),
  isLoading = false,
}: {
  children: React.ReactNode;
  flowName: string;
  asset_page_route: string;
  backgroundColor?: string;
  isLoading?: boolean;
}) {
  const {uiInteraction} = useUiInteractionEvent();
  const {navigate} = useNavigation();
  const displayAsset = (asset: SelectedAsset) => {
    const removeAsset = () => {
      const assets = selectedAssets.filter((a) => a.id !== asset.id);
      contentStudioActions.setSelectedAssets(assets);
    };
    if (asset.isUploading) {
      return (
        <ThumbnailDisplay
          file={Object.values(files).find((f) => f.id === asset.id)}
          width={92}
          height={92}
          onClose={isLoading ? undefined : removeAsset}
        />
      );
    }
    return (
      <ThumbnailDisplay
        thumbnail={asset.thumbnail}
        width={92}
        height={92}
        onClose={isLoading ? undefined : removeAsset}
      />
    );
  };
  const {selectedAssets, files} = contentStudioStore(
    useShallow((state) => ({
      selectedAssets: state.selectedAssets,
      files: state.files,
    }))
  );
  return (
    <Stack width="100%" overflow="hidden">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        padding="24px 24px 12px 24px"
        sx={{
          backgroundColor: designSystemToken('semantic.bg.secondary'),
        }}
      >
        <Headline size="xxs">
          {translate('selected_files', {
            files: selectedAssets.length,
            count: selectedAssets.length,
          })}
        </Headline>
        <Button
          appearance="neutral"
          mode="plain"
          size="small"
          icon={
            <Icon name="Actions-Add-Small1" size="small" appearance="primary" />
          }
          iconPosition="end"
          onClick={() => {
            uiInteraction({
              item_name: 'add_files',
              interaction_location: `${flowName}_editor`,
              flow_name: flowName,
            });
            navigate(asset_page_route);
          }}
        >
          {translate('add')}
        </Button>
      </Stack>
      <Box
        width="100%"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="190px"
        sx={{
          backgroundColor: designSystemToken('semantic.bg.secondary'),
        }}
        borderBottom={`1px solid ${designSystemToken('semantic.bg.tertiary')}`}
      >
        <Stack
          direction="row"
          gap="16px"
          overflow="auto"
          paddingBottom="24px"
          width="98%"
          paddingTop="22px"
        >
          {selectedAssets.map((asset) => (
            <Stack
              key={asset.id}
              direction="column"
              gap="8px"
              alignItems="center"
              maxWidth="92px"
            >
              {displayAsset(asset)}
              <Headline
                width="100%"
                size="xxs"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {asset.fileName}
              </Headline>
            </Stack>
          ))}
        </Stack>
      </Box>
      <Box
        sx={{
          backgroundColor,
          width: '100%',
          height: '100%',
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}

export default ManyToManyEditor;
